import type { DialogProps } from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import CollectionSelectionDialog from '@/components/collection-selection-dialog';
import { useCollections } from '@/hooks/use-collections';
import { useParagraphCommentCounts } from '@/hooks/use-paragraph-comment-counts';
import { useReadingV2 } from '@/hooks/use-reading-v2';
import type { ReaderType } from '@/types';
import type { Chapter, TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import { ReaderProgressBar } from '@/features/reader/components/reader-progress-bar';
import { ReaderSettingsPanel } from '@/features/reader/components/reader-display-settings/reader-display-settings';
import { ReaderToolbar } from '@/features/reader/components/toolbar-reader/reader-toolbar';
import { useReaderToolbarVisibility } from '@/features/reader/components/toolbar-reader/hooks/use-reader-toolbar-visibility';
import { getReaderTheme } from '@/features/reader/components/reader-display-settings/utils/reader-display-settings';
import { useReaderDisplaySettings } from '@/features/reader/reader-display-settings/hooks/use-reader-display-settings';
import { applyReaderTheme } from '@/features/reader/utils/apply-reader-theme.util';
import { LexicalContentRenderer } from '../lexical-content-renderer';
import { ChapterHeader } from './chapter-header';
import { CommentDrawer } from './comment-drawer';
import { FanficReviews } from '@/components/fanfic-reviews';
import { DonatModal } from '../donat-modal';
import { LockedChapterContent } from '@/features/original-sale/components';
import { useCloudPaymentsPayment } from '@/features/cloudpayments';
import { saveReadingResumeBookmark } from '@/features/fanfic-page/utils/reading-resume.storage';
import {
  getLastPublishedChapterIndex,
  shouldPromptWorkReview,
} from '@/features/fanfic-page/utils/last-published-chapter.util';
import { FinishReadingReviewDialog } from '@/features/reader/components/finish-reading-review-dialog';
import { useModalStore } from '@/store';
import { useAuth } from '@/hooks/use-auth';
import { cn } from '@/lib/utils';

interface FullscreenReaderV2Props extends DialogProps {
  type?: ReaderType;
  chaptersWithContent: Chapter[];
  chaptersWithoutContent: TChapterWithoutContent[];
  selectedChapterId: string;
  setSelectedChapterId: (chapterId: string) => void;
  fanficId?: string;
  author?: {
    id?: number;
    name?: string;
    username?: string;
    avatar?: {
      url?: string;
      thumbnailURL?: string;
    };
    donation?: {
      donationUserId?: string | null;
      hasCard: boolean;
    };
  } | null;
  views?: number;
  comments?: number;
  /** UI sync only — must not trigger another reading-progress PUT. */
  onProgressSync?: (fanficId: string, chapterIndex: number, progress: number) => void;
  price?: number | null;
  postId?: number;
  onLoadMoreChapters?: (direction: 'next' | 'prev') => Promise<void>;
  isLoadingMoreChapters?: boolean;
  onChapterSelect?: (chapterId: string) => Promise<void>;
  ssrChapterContent?: Chapter;
  workPathPrefix?: '/fanfics' | '/originals';
  isRating18?: boolean;
  fetchProgress?: boolean;
  initialProgressByChapter?: { chapterIndex: number; progress: number }[];
  onLeaveReview?: () => void;
}

const READER_FOOTER_HEIGHT_PX = 56;

const isChromeToggleIgnoredTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) return true;
  return Boolean(
    target.closest(
      [
        '[data-reader-chrome]',
        '[data-no-chrome-toggle]',
        '[data-comment-trigger]',
        'a',
        'button',
        'input',
        'textarea',
        'label',
        'select',
        '[role="button"]',
        '[role="dialog"]',
        '[data-radix-popper-content-wrapper]',
      ].join(','),
    ),
  );
};

// Если ты пришёл работать дальше над этой фичей. Для комментариев используй компонент CommentDrawer
export const FullscreenReaderV2 = ({
  chaptersWithContent,
  chaptersWithoutContent,
  selectedChapterId,
  setSelectedChapterId,
  type = 'full',
  fanficId = '',
  author,
  views = 0,
  comments = 0,
  onProgressSync,
  price,
  postId,
  onLoadMoreChapters,
  isLoadingMoreChapters = false,
  onChapterSelect,
  ssrChapterContent,
  workPathPrefix = '/fanfics',
  isRating18 = false,
  fetchProgress = true,
  initialProgressByChapter,
  onLeaveReview,
  ...props
}: FullscreenReaderV2Props) => {
  // Get chapter metadata (without content) for navigation and display
  const chapter = chaptersWithoutContent.find((chapter) => chapter.id === selectedChapterId);
  // Get chapter with content for rendering - prioritize SSR content if available
  const chapterWithContent =
    ssrChapterContent?.id === selectedChapterId
      ? ssrChapterContent
      : chaptersWithContent.find((chapter) => chapter.id === selectedChapterId);
  const [isOpenSettings, setIsOpenSettings] = useState(false);
  const [chapterMenuOpen, setChapterMenuOpen] = useState(false);
  const [collectionMenuOpen, setCollectionMenuOpen] = useState(false);
  const [toolbarHovered, setToolbarHovered] = useState(false);
  const { settings, setTheme, setFontSize, setLineHeight, resetSettings } =
    useReaderDisplaySettings();
  const theme = getReaderTheme(settings.theme);
  const [selectedParagraph, setSelectedParagraph] = useState<{
    paragraphId: string;
    text: string;
    index: number;
  } | null>(null);
  const [isCommentDrawerOpen, setIsCommentDrawerOpen] = useState(false);
  const [isDonatModalOpen, setIsDonatModalOpen] = useState(false);
  const [hasShownFirstChapterDialog, setHasShownFirstChapterDialog] = useState(false);
  const chromeResetKey = `${selectedChapterId}:${chapterWithContent?.id ?? ''}`;
  const { setIsOpenAuthDialog } = useModalStore();
  const { isAuthenticated } = useAuth();

  const resolvedPostId = postId || (fanficId ? Number(fanficId) : undefined);
  const { counts: paragraphCommentsCount, refetch: refetchParagraphCounts } =
    useParagraphCommentCounts(
      type === 'full' ? resolvedPostId : undefined,
      type === 'full' ? selectedChapterId : undefined,
    );

  const { data: session } = useSession();

  // Use CloudPayments payment hook for locked chapters
  const { handleCloudPaymentsPayment, isProcessingPayment } = useCloudPaymentsPayment({
    postId: postId || (fanficId ? parseInt(fanficId) : 0),
  });

  const handleBuyOriginalClick = async (mouseEvent: React.MouseEvent<HTMLButtonElement>) => {
    if (isAuthenticated) {
      await handleCloudPaymentsPayment(mouseEvent);
    } else {
      setIsOpenAuthDialog(true, 'Чтобы купить работу, необходимо авторизоваться');
    }
  };

  const rootRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentEndRef = useRef<HTMLDivElement>(null);
  const finishReviewReturnFocusRef = useRef<HTMLElement | null>(null);
  const promptedReviewChaptersRef = useRef<Set<number>>(new Set());
  const [isFinishReviewOpen, setIsFinishReviewOpen] = useState(false);
  const chapterMeasureRef = useRef<HTMLDivElement>(null);
  const settingsButtonRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const node = rootRef.current;
    if (!node) return;
    applyReaderTheme(node, theme);
  }, [theme]);

  const currentChapterIndex = chaptersWithoutContent.findIndex(
    (chapter) => chapter.id === selectedChapterId,
  );
  const hasNextChapter = currentChapterIndex < chaptersWithoutContent.length - 1;
  const hasPreviousChapter = currentChapterIndex > 0;

  const toolbarPinned = isOpenSettings || chapterMenuOpen || collectionMenuOpen || toolbarHovered;
  const { visible: isToolbarVisible, toggle: toggleToolbar } = useReaderToolbarVisibility({
    containerRef,
    pinned: toolbarPinned,
    resetKey: chromeResetKey,
  });

  const handleEmptyAreaToggleChrome = useCallback(
    (event: React.MouseEvent) => {
      if (isChromeToggleIgnoredTarget(event.target)) return;
      if (isToolbarVisible) setIsOpenSettings(false);
      toggleToolbar();
    },
    [isToolbarVisible, toggleToolbar],
  );

  const prepareChapterNavigation = useCallback(() => {
    setIsOpenSettings(false);
    setSelectedParagraph(null);
    setIsCommentDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (!fanficId || !selectedChapterId || currentChapterIndex < 0) {
      return;
    }

    saveReadingResumeBookmark({
      postId: fanficId,
      chapterId: selectedChapterId,
      chapterIndex: currentChapterIndex,
    });
  }, [fanficId, selectedChapterId, currentChapterIndex]);

  const goToNextChapter = useCallback(async () => {
    const nextChapterIndex = currentChapterIndex + 1;
    if (nextChapterIndex < chaptersWithoutContent.length) {
      const nextChapterId = chaptersWithoutContent[nextChapterIndex].id;
      prepareChapterNavigation();
      setSelectedChapterId(nextChapterId);

      // Load content for next chapter if not already loaded
      if (onChapterSelect) {
        await onChapterSelect(nextChapterId);
      }

      // Check if we're at the last loaded chapter and need to load more
      if (nextChapterIndex === chaptersWithoutContent.length - 1 && onLoadMoreChapters) {
        await onLoadMoreChapters('next');
      }
    }
  }, [
    currentChapterIndex,
    chaptersWithoutContent,
    setSelectedChapterId,
    onLoadMoreChapters,
    onChapterSelect,
    prepareChapterNavigation,
  ]);

  const goToPreviousChapter = useCallback(async () => {
    const previousChapterIndex = currentChapterIndex - 1;
    if (previousChapterIndex >= 0) {
      const previousChapterId = chaptersWithoutContent[previousChapterIndex].id;
      prepareChapterNavigation();
      setSelectedChapterId(previousChapterId);

      // Load content for previous chapter if not already loaded
      if (onChapterSelect) {
        await onChapterSelect(previousChapterId);
      }

      // Check if we're at the first loaded chapter and need to load previous chapters
      if (previousChapterIndex === 0 && onLoadMoreChapters) {
        await onLoadMoreChapters('prev');
      }
    }
  }, [
    currentChapterIndex,
    chaptersWithoutContent,
    setSelectedChapterId,
    onLoadMoreChapters,
    onChapterSelect,
    prepareChapterNavigation,
  ]);

  // Optimized chapter change handler for dropdown
  const handleChapterChange = useCallback(
    (chapterId: string) => {
      prepareChapterNavigation();
      setSelectedChapterId(chapterId);

      // Load content for selected chapter
      if (onChapterSelect) {
        onChapterSelect(chapterId);
      }
    },
    [onChapterSelect, setSelectedChapterId, prepareChapterNavigation],
  );

  const handleChapterCompleted = useCallback(
    (completedChapterIndex: number) => {
      if (type !== 'full' || !fanficId) return;
      if (promptedReviewChaptersRef.current.has(completedChapterIndex)) return;

      const savedProgress =
        initialProgressByChapter?.find((item) => item.chapterIndex === completedChapterIndex)
          ?.progress ?? 0;
      const shouldOpen = shouldPromptWorkReview({
        chapterIndex: completedChapterIndex,
        lastPublishedChapterIndex: getLastPublishedChapterIndex(chaptersWithoutContent),
        previousProgress: savedProgress >= 100 ? 100 : 0,
        nextProgress: 100,
      });
      if (!shouldOpen) return;

      promptedReviewChaptersRef.current.add(completedChapterIndex);
      const activeElement = document.activeElement;
      finishReviewReturnFocusRef.current =
        activeElement instanceof HTMLElement && activeElement !== document.body
          ? activeElement
          : containerRef.current;
      setIsFinishReviewOpen(true);
    },
    [chaptersWithoutContent, fanficId, initialProgressByChapter, type],
  );

  const { progress } = useReadingV2({
    fanficId: fanficId,
    containerRef,
    contentEndRef,
    layoutRef: chapterMeasureRef,
    chapterIndex: currentChapterIndex,
    autoScroll: !!fanficId,
    autoUpdate: !!fanficId,
    fetchProgress,
    initialProgressByChapter,
    onProgressSync,
    onChapterCompleted: handleChapterCompleted,
  });

  const { collectionData, addToCollection, updateStatus, removeFromCollection } = useCollections(
    fanficId || '',
  );

  // Derived state: determine if collection dialog should be shown
  // Logic: show dialog at 80% progress of first chapter if fanfic is not in collection
  const shouldShowCollectionDialog =
    type === 'full' &&
    !!fanficId &&
    currentChapterIndex === 0 &&
    progress >= 80 &&
    !collectionData.isInCollection &&
    !hasShownFirstChapterDialog &&
    !!session;

  const handleCollectionDialogClose = () => {
    setHasShownFirstChapterDialog(true);
  };

  const handleCollectionConfirm = async (status: string, isPrivate: boolean) => {
    await addToCollection(status, isPrivate);
    setHasShownFirstChapterDialog(true);
  };

  // Generate share link for current chapter (client-side only)
  const shareLink =
    typeof window !== 'undefined'
      ? fanficId
        ? `${window.location.origin}${workPathPrefix}/${fanficId}/read${
            selectedChapterId ? `/${selectedChapterId}` : ''
          }`
        : window.location.href
      : '';

  return (
    <div
      ref={rootRef}
      className="group/reader fixed inset-0 z-50 overflow-hidden bg-[var(--reader-bg)] text-[var(--reader-text)]"
      data-reader-tone={theme.tone}
      style={{
        height: '100dvh',
        maxHeight: '100dvh',
        background: theme.background,
        color: theme.text,
        ['--reader-bg' as string]: theme.background,
        ['--reader-text' as string]: theme.text,
        ['--reader-surface' as string]: theme.surface,
        ['--reader-muted' as string]: theme.muted,
      }}
    >
      <div className="relative h-full w-full overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'var(--reader-bg)' }}>
          <div
            className="h-full overflow-x-hidden overflow-y-auto overscroll-contain px-2.5 pt-24 outline-none [-webkit-overflow-scrolling:touch] max-sm:px-2 max-sm:pt-[84px]"
            ref={containerRef}
            tabIndex={-1}
            onClick={handleEmptyAreaToggleChrome}
            style={{
              paddingBottom: `calc(${READER_FOOTER_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
            }}
          >
            <div className="mx-auto min-w-0 max-w-[820px] pt-2" data-no-chrome-toggle>
              <ChapterHeader
                type={type}
                title={chapter?.title || ''}
                views={views}
                comments={comments}
                author={author}
                textColor="var(--reader-text)"
                linkToShare={shareLink}
                onSupportClick={() => setIsDonatModalOpen(true)}
              />
            </div>
            {chapter?.isLocked ? (
              <div className="mx-auto min-w-0 max-w-[820px]" data-no-chrome-toggle>
                <LockedChapterContent
                  price={price || 0}
                  onBuyClick={handleBuyOriginalClick}
                  isProcessingPayment={isProcessingPayment}
                />
              </div>
            ) : !chapterWithContent ? (
              <div
                className="mx-auto mt-8 flex min-h-[300px] max-w-[820px] items-center justify-center"
                data-no-chrome-toggle
              >
                <Loader2 className="h-12 w-12 animate-spin text-[#505CFF]" />
              </div>
            ) : (
              <>
                <div
                  ref={chapterMeasureRef}
                  className="mx-auto min-w-0 max-w-[820px]"
                  data-no-chrome-toggle
                >
                  <div
                    className="mt-8 break-words [overflow-wrap:anywhere]"
                    style={{
                      color: 'var(--reader-text)',
                      fontSize: `${settings.fontSize}px`,
                      lineHeight: settings.lineHeight,
                    }}
                  >
                    <LexicalContentRenderer
                      content={chapterWithContent?.content}
                      textColor="var(--reader-text)"
                      inheritTypography
                      selectedParagraphId={selectedParagraph?.paragraphId}
                      getParagraphCommentCount={(paragraphId) =>
                        paragraphCommentsCount[paragraphId] || 0
                      }
                      onCommentIconClick={(paragraphId, text, index) => {
                        setSelectedParagraph({ paragraphId, text, index });
                        setIsCommentDrawerOpen(true);
                      }}
                    />
                  </div>
                  {/* 100% is the end of chapter text and its footnotes, before comments. */}
                  <div ref={contentEndRef} />
                </div>

                {hasNextChapter && (
                  <div
                    className="mx-auto flex max-w-[820px] justify-center pb-2 pt-8"
                    data-no-chrome-toggle
                  >
                    <button
                      type="button"
                      onClick={() => {
                        void goToNextChapter();
                      }}
                      disabled={isLoadingMoreChapters}
                      className={cn(
                        'flex h-10 items-center justify-center whitespace-nowrap rounded-xl px-8 py-3 font-medium text-white',
                        'bg-gradient-to-b from-white/20 bg-[#ff3dae]',
                        'shadow-[0px_10px_30px_0px_rgba(0,0,0,0.25)]',
                        'hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60',
                      )}
                    >
                      {isLoadingMoreChapters ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        'Следующая глава'
                      )}
                    </button>
                  </div>
                )}

                {fanficId && type === 'full' && selectedChapterId && (
                  <div
                    className="mx-auto min-w-0 max-w-[820px] pt-6 [overflow-anchor:none]"
                    data-no-chrome-toggle
                  >
                    <h2
                      className="mb-4 text-[26px] font-black"
                      style={{ color: 'var(--reader-text)' }}
                    >
                      КОММЕНТАРИИ
                    </h2>
                    <div className="min-w-0">
                      <FanficReviews
                        key={selectedChapterId}
                        fanficId={Number(fanficId)}
                        chapterId={selectedChapterId}
                        className="w-full"
                        hideTitle={true}
                      />
                    </div>
                  </div>
                )}
                <div className="pb-8" />
              </>
            )}
          </div>
        </div>

        {fanficId && type === 'full' && chapter && isCommentDrawerOpen && (
          <CommentDrawer
            chapterTitle={chapter.title || ''}
            currentChapterIndex={currentChapterIndex}
            totalChapters={chaptersWithoutContent.length}
            selectedParagraph={selectedParagraph}
            fanficId={fanficId}
            chapterId={selectedChapterId}
            postId={resolvedPostId}
            open={isCommentDrawerOpen}
            onOpenChange={(open) => {
              setIsCommentDrawerOpen(open);
              if (!open) {
                setSelectedParagraph(null);
              }
            }}
            onParagraphSelect={setSelectedParagraph}
            onCommentsChange={() => {
              void refetchParagraphCounts();
            }}
          />
        )}

        <ReaderProgressBar value={progress} />

        <ReaderToolbar
          tone={theme.tone}
          visible={isToolbarVisible}
          chapters={chaptersWithoutContent}
          selectedChapterId={selectedChapterId}
          onChapterChange={handleChapterChange}
          onChaptersOpenChange={setChapterMenuOpen}
          workId={fanficId}
          workPathPrefix={workPathPrefix}
          isRating18={isRating18}
          showAddAction={type === 'full' && Boolean(fanficId)}
          fanficId={fanficId}
          collectionStatus={collectionData.status}
          isInCollection={collectionData.isInCollection}
          chaptersCount={chaptersWithoutContent.length}
          onCollectionPresenceChange={setCollectionMenuOpen}
          onCollectionStatusChange={(status, isPrivate) => updateStatus(status, isPrivate)}
          onAddToCollection={(status, isPrivate) => addToCollection(status, isPrivate)}
          onRemoveFromCollection={removeFromCollection}
          settingsButtonRef={settingsButtonRef}
          settingsOpen={isOpenSettings}
          onSettingsToggle={() => setIsOpenSettings((open) => !open)}
          onClose={() => props.onOpenChange?.(false)}
          onPinnedChange={setToolbarHovered}
        />

        {isOpenSettings && (
          <ReaderSettingsPanel
            settings={settings}
            onThemeChange={setTheme}
            onFontSizeChange={setFontSize}
            onLineHeightChange={setLineHeight}
            onReset={resetSettings}
            onClose={() => setIsOpenSettings(false)}
            returnFocusRef={settingsButtonRef}
          />
        )}

        <div
          data-reader-chrome
          className={cn(
            'absolute inset-x-0 bottom-0 z-40 px-2.5 max-sm:px-2',
            'will-change-[transform,opacity] transition-[transform,opacity] duration-300 ease-in-out motion-reduce:transition-none',
            !isToolbarVisible && 'pointer-events-none',
          )}
          style={{
            height: `calc(${READER_FOOTER_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`,
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            background: 'var(--reader-bg)',
          }}
          inert={isToolbarVisible ? undefined : true}
          aria-hidden={isToolbarVisible ? undefined : true}
        >
          <div className="flex h-[56px] w-full items-center justify-center">
            <div
              className="grid h-11 w-full max-w-[820px] grid-cols-[max-content_1fr_max-content] rounded-[8px]"
              style={{ background: 'var(--reader-surface)', color: 'var(--reader-text)' }}
            >
              <button
                type="button"
                disabled={!hasPreviousChapter || isLoadingMoreChapters}
                className="flex items-center gap-1 pl-5 text-sm font-medium enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                onClick={goToPreviousChapter}
              >
                <ChevronLeft className="size-5" />
                <span className="max-sm:sr-only">Предыдущая глава</span>
              </button>
              <div
                className="flex items-center justify-center text-sm font-medium leading-[140%]"
                style={{ color: 'var(--reader-muted)' }}
              >
                {isLoadingMoreChapters ? (
                  <Loader2 className="h-5 w-5 animate-spin text-[#505CFF]" />
                ) : (
                  <>
                    {currentChapterIndex + 1} из {chaptersWithoutContent.length}
                  </>
                )}
              </div>
              <button
                type="button"
                disabled={!hasNextChapter || isLoadingMoreChapters}
                className="flex items-center gap-1 pr-5 text-sm font-medium enabled:cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                onClick={goToNextChapter}
                style={{
                  color: hasNextChapter ? '#505CFF' : 'var(--reader-text)',
                }}
              >
                <span className="max-sm:sr-only">Следующая глава</span>
                <ChevronRight className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {type === 'full' && (
        <FinishReadingReviewDialog
          open={isFinishReviewOpen}
          onOpenChange={setIsFinishReviewOpen}
          onLeaveReview={() => {
            setIsFinishReviewOpen(false);
            onLeaveReview?.();
          }}
          returnFocusRef={finishReviewReturnFocusRef}
        />
      )}

      {/* Donat Modal */}
      <DonatModal
        isOpen={isDonatModalOpen}
        onClose={() => setIsDonatModalOpen(false)}
        author={author || null}
      />

      {/* Collection Selection Dialog - показывается при 80% прочтения первой главы */}
      {type === 'full' && fanficId && shouldShowCollectionDialog && (
        <CollectionSelectionDialog
          isOpen={true}
          onClose={handleCollectionDialogClose}
          onConfirm={handleCollectionConfirm}
          postId={fanficId}
          chaptersCount={chaptersWithoutContent.length}
          title="ДОБАВИТЬ В ВАШУ БИБЛИОТЕКУ?"
        />
      )}
    </div>
  );
};
